import { useLex } from "../../lib/theme/ThemeProvider";
import { useState, type FormEvent } from "react";
import { useParams, useNavigate } from "react-router-dom";
import CommandLayout from "../../components/war-room/CommandLayout";
import { useCase } from "../../hooks/useCase";
import type { Case } from "../../hooks/useCases";
import { useCaseFilings, useFilingSections, type FilingRow } from "../../hooks/useCaseFilings";
import {
  useCaseParties,
  groupByParty,
  type GroupedParty,
} from "../../hooks/useCaseParties";
import {
  useRavenDossiers,
  useDispatchRaven,
  type RavenDossier,
  type RavenSubjectType,
} from "../../hooks/useRavenDossiers";
import { useExtractMetadata } from "../../hooks/useExtractMetadata";

/**
 * Raven — recon dossier. v0 surfaces what the system already knows
 * about the case (filings ingested, parties on the field, structural
 * shape of the operative pleading). External recon (judges, opposing
 * counsel web research) is later work — those dispatch slots are
 * present but disabled.
 */
function Raven() {
  const { caseId } = useParams<{ caseId: string }>();

  return (
    <CommandLayout caseId={caseId} variant="doctrine" currentCommand="raven">
      <PageHeader />

      {caseId ? (
        <>
          <FilingsPanel caseId={caseId} />
          <PartiesPanel caseId={caseId} />
          <StructurePanel caseId={caseId} />
          <ExternalReconPanel caseId={caseId} />
        </>
      ) : null}
    </CommandLayout>
  );
}

// ============================================================
// Header
// ============================================================

function PageHeader() {
  return (
    <header className="rounded border border-steel-dark bg-iron/70 px-6 py-6 backdrop-blur-sm">
      <p className="mb-2 font-display text-base font-light uppercase tracking-[0.18em] text-bone-mute">
        // call the raven
      </p>
      <h2
        className="font-display text-2xl font-light leading-tight tracking-tight text-bone md:text-3xl"
        style={{ textShadow: "0 2px 16px rgba(0,0,0,0.6)" }}
      >
        Dispatch the bird. Return with intel.
      </h2>
      <p className="mt-3 max-w-3xl font-display text-sm leading-relaxed text-bone-dim">
        What the system knows about this case — filings, parties, the
        structural shape of the operative pleading. External recon
        (judges, opposing counsel profiles) dispatches below.
      </p>
    </header>
  );
}

// ============================================================
// Filings panel
// ============================================================

function FilingsPanel({ caseId }: { caseId: string }) {
  const { data, isLoading, error } = useCaseFilings(caseId);
  const navigate = useNavigate();

  return (
    <section className="rounded border border-steel-dark bg-iron/70 backdrop-blur-sm">
      <div className="flex items-center justify-between border-b border-steel-dark/70 px-5 py-3 font-display text-base font-light uppercase tracking-[0.16em]">
        <span className="text-bone-dim">filings // on file</span>
        <span className="text-bone-mute/70">
          {isLoading ? "loading" : `${data?.length ?? 0} ingested`}
        </span>
      </div>
      <div className="px-5 py-4">
        {isLoading ? (
          <Skeleton label="opening the docket" />
        ) : error ? (
          <ErrorRow message={error.message} />
        ) : !data || data.length === 0 ? (
          <EmptyRow
            label="no filings on this case"
            cta="Upload one →"
            onCta={() => navigate("/upload")}
          />
        ) : (
          <ul className="space-y-2">
            {data.map((f) => (
              <FilingRowDisplay key={f.id} filing={f} />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function FilingRowDisplay({ filing }: { filing: FilingRow }) {
  return (
    <li className="rounded border border-steel-dark/70 bg-iron-2/40 px-4 py-3 transition-colors hover:border-blue-bright/40">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-display text-base font-light uppercase tracking-[0.12em] text-bone-mute">
            {filing.filing_kind.replace(/_/g, " ")}
            {filing.filed_by ? ` // filed by ${filing.filed_by}` : ""}
          </p>
          <p className="mt-1 truncate font-display text-base font-light text-bone">
            {filing.title}
          </p>
          <p className="mt-1 font-display text-base font-light uppercase tracking-[0.1em] text-bone-mute/70">
            {filing.filed_at
              ? new Date(filing.filed_at).toLocaleDateString("en-US", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })
              : "filed date unknown"}
          </p>
        </div>
        {filing.is_operative ? (
          <span className="shrink-0 rounded border border-ember-bright/60 px-2 py-0.5 font-display text-sm font-light uppercase tracking-[0.12em] text-ember-bright">
            operative
          </span>
        ) : (
          <span className="shrink-0 rounded border border-bone-mute/40 px-2 py-0.5 font-display text-sm font-light uppercase tracking-[0.12em] text-bone-mute">
            superseded
          </span>
        )}
      </div>
    </li>
  );
}

// ============================================================
// Parties panel — leaner mirror of the Battlemap layout
// ============================================================

function PartiesPanel({ caseId }: { caseId: string }) {
  const { data, isLoading, error } = useCaseParties(caseId);

  if (isLoading)
    return (
      <PanelShell label="parties // on the field" status="loading">
        <Skeleton label="gathering the field" />
      </PanelShell>
    );
  if (error)
    return (
      <PanelShell label="parties // on the field" status="error">
        <ErrorRow message={error.message} />
      </PanelShell>
    );
  if (!data || data.length === 0)
    return (
      <PanelShell label="parties // on the field" status="empty">
        <p className="font-display text-sm text-bone-dim">
          No parties yet. Ingest the operative pleading from /upload to
          populate.
        </p>
      </PanelShell>
    );

  const parties = groupByParty(data);
  const plaintiffs = parties.filter((p) => p.role === "plaintiff");
  const defendants = parties.filter((p) => p.role === "defendant");
  const others = parties.filter(
    (p) => p.role !== "plaintiff" && p.role !== "defendant",
  );

  return (
    <PanelShell label="parties // on the field" status={`${parties.length} parties`}>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <PartyGroup parties={plaintiffs} label="plaintiffs" accent="blue" />
        <PartyGroup parties={defendants} label="defendants" accent="ember" />
        {others.length > 0 ? (
          <div className="md:col-span-2">
            <PartyGroup parties={others} label="other parties" accent="bone" />
          </div>
        ) : null}
      </div>
    </PanelShell>
  );
}

function PartyGroup({
  parties,
  label,
  accent,
}: {
  parties: GroupedParty[];
  label: string;
  accent: "blue" | "ember" | "bone";
}) {
  const accentText =
    accent === "blue"
      ? "text-blue-bright"
      : accent === "ember"
        ? "text-ember-bright"
        : "text-bone";
  return (
    <div>
      <p
        className={`mb-2 font-display text-base font-light uppercase tracking-[0.16em] ${accentText}`}
      >
        // {label} // {parties.length}
      </p>
      {parties.length === 0 ? (
        <p className="font-display text-base font-light uppercase tracking-[0.12em] text-bone-mute/60">
          (none)
        </p>
      ) : (
        <ul className="space-y-1.5">
          {parties.map((p) => (
            <li
              key={p.party_id}
              className="rounded border border-steel-dark bg-iron-2/40 px-3 py-2"
            >
              <p className="font-display text-sm font-light text-bone">
                {p.display_name}
              </p>
              {p.capacities.length > 0 ? (
                <p className="mt-0.5 font-display text-sm font-light uppercase tracking-[0.12em] text-bone-mute">
                  {[...p.capacities].sort().join(" · ")}
                </p>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ============================================================
// Operative pleading structure
// ============================================================

function StructurePanel({ caseId }: { caseId: string }) {
  const { data: filings } = useCaseFilings(caseId);
  const operative = filings?.find((f) => f.is_operative) ?? filings?.[0];
  const { data: sections, isLoading, error } = useFilingSections(operative?.id);

  return (
    <PanelShell
      label={
        operative
          ? `structure // ${operative.filing_kind.replace(/_/g, " ")}`
          : "structure"
      }
      status={
        isLoading
          ? "loading"
          : error
            ? "error"
            : `${sections?.length ?? 0} sections`
      }
    >
      {!operative ? (
        <p className="font-display text-sm text-bone-dim">
          No operative pleading identified yet. Upload one to see its
          structure.
        </p>
      ) : isLoading ? (
        <Skeleton label="reading the index" />
      ) : error ? (
        <ErrorRow message={error.message} />
      ) : !sections || sections.length === 0 ? (
        <p className="font-display text-sm text-bone-dim">
          The filing parsed without sections — unusual.
        </p>
      ) : (
        <ul className="space-y-1">
          {sections.map((s) => (
            <li
              key={s.id}
              className="flex items-center justify-between gap-3 border-l-2 border-steel-dark/70 pl-3 hover:border-blue-bright/60"
            >
              <div className="min-w-0 flex-1">
                <p className="font-display text-base font-light uppercase tracking-[0.12em] text-bone-mute">
                  {s.section_kind.replace(/_/g, " ")}
                  {s.ordinal !== null ? ` // ordinal ${s.ordinal}` : ""}
                </p>
                <p className="truncate font-display text-sm font-light text-bone">
                  {s.title ?? "(untitled)"}
                </p>
                {s.legal_basis ? (
                  <p className="mt-0.5 font-display text-sm italic text-bone-dim">
                    {s.legal_basis}
                  </p>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </PanelShell>
  );
}

// ============================================================
// External recon — real dispatch + dossier display
// ============================================================

const SUBJECT_TYPES: { value: RavenSubjectType; label: string }[] = [
  { value: "opposing_counsel", label: "Opposing counsel" },
  { value: "assigned_judge", label: "Assigned judge" },
  { value: "opposing_party", label: "Opposing party" },
  { value: "adjacent_party", label: "Adjacent party" },
  { value: "expert_witness", label: "Expert witness" },
  { value: "other", label: "Other" },
];

function ExternalReconPanel({ caseId }: { caseId: string }) {
  const lex = useLex();
  const { data: dossiers, isLoading, error } = useRavenDossiers(caseId);
  const dispatch = useDispatchRaven();
  const extract = useExtractMetadata();
  const { data: caseRow } = useCase(caseId);
  const { data: partyRows } = useCaseParties(caseId);
  const [open, setOpen] = useState(false);

  const hasMetadata =
    (caseRow?.parsed_metadata?.attorneys?.length ?? 0) > 0 ||
    !!caseRow?.parsed_metadata?.judge;

  const groupedParties = partyRows ? groupByParty(partyRows) : [];

  async function handleDispatch(input: {
    subjectType: RavenSubjectType;
    subjectName: string;
    subjectContext: string;
  }) {
    if (!caseRow) return;
    await dispatch.mutateAsync({
      caseId,
      caseCaption: caseRow.caption,
      caseJurisdiction: caseRow.jurisdiction,
      caseNumber: caseRow.case_number,
      caseCourt: caseRow.court,
      caseParties: groupedParties.map((p) => ({
        display_name: p.display_name,
        role: p.role,
        capacities: p.capacities,
      })),
      subjectType: input.subjectType,
      subjectName: input.subjectName || undefined,
      subjectContext: input.subjectContext || undefined,
    });
    setOpen(false);
  }

  return (
    <PanelShell
      label="external recon // dossiers"
      status={
        isLoading
          ? "loading"
          : error
            ? "error"
            : `${dossiers?.length ?? 0} dispatched`
      }
    >
      <div className="mb-4 flex items-center justify-between gap-3">
        <p className="font-display text-sm text-bone-dim">
          Send the raven on a subject — opposing counsel, judge, party.
          Compiles a cited dossier from public sources.
        </p>
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            onClick={() => extract.mutate({ caseId })}
            disabled={extract.isPending}
            title="Extract counsel and judge from the filing (fast, ~15s)"
            className="border border-bone-mute/60 bg-iron-2/40 px-3 py-2 font-display text-base font-light uppercase tracking-[0.14em] text-bone-dim hover:border-bone-mute hover:text-bone disabled:cursor-wait disabled:opacity-50"
          >
            {extract.isPending
              ? "extracting…"
              : hasMetadata
                ? "↻ re-extract"
                : "extract from filing"}
          </button>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="border border-blue-bright/60 bg-iron-2/60 px-4 py-2 font-display text-base font-light uppercase tracking-[0.14em] text-bone hover:border-blue-bright hover:bg-iron-2"
          >
            {open ? "× close" : lex.ui.ravenOpen}
          </button>
        </div>
      </div>

      {extract.error ? (
        <p className="mb-3 font-display text-base font-light uppercase tracking-[0.12em] text-ember-bright">
          // extract failed: {extract.error.message}
        </p>
      ) : null}

      {open ? (
        <DispatchForm
          parties={groupedParties}
          parsedMetadata={caseRow?.parsed_metadata}
          onSubmit={handleDispatch}
          isPending={dispatch.isPending}
          error={dispatch.error?.message}
        />
      ) : null}

      {isLoading ? (
        <Skeleton label="opening the archive" />
      ) : error ? (
        <ErrorRow message={error.message} />
      ) : !dossiers || dossiers.length === 0 ? (
        <p className="font-display text-sm italic text-bone-mute">
          No dossiers yet. Dispatch the raven on a subject above.
        </p>
      ) : (
        <div className="space-y-3">
          {dossiers.map((d) => (
            <DossierCard key={d.id} dossier={d} />
          ))}
        </div>
      )}
    </PanelShell>
  );
}

/**
 * For each subject type, decide whether the Raven needs a name input,
 * and what to suggest from the case data when relevant.
 */
const SELF_IDENTIFYING_TYPES = new Set<RavenSubjectType>([
  "opposing_counsel",
  "assigned_judge",
]);

interface NameSuggestion {
  key: string;
  display_name: string;
  /** Optional sub-label for chip (firm, capacity, etc.). */
  sub?: string;
}

/** Build name suggestions for a subject type from case data + parsed metadata. */
function suggestionsFor(
  subjectType: RavenSubjectType,
  parties: GroupedParty[],
  parsedMetadata: Case["parsed_metadata"] | undefined,
): NameSuggestion[] {
  // For v0 dogfood, assume user is on the defense side.
  // Opposing party = plaintiff(s). Opposing counsel = plaintiff's counsel.
  if (subjectType === "opposing_party") {
    return parties
      .filter((p) => p.role === "plaintiff")
      .map((p) => ({
        key: p.party_id,
        display_name: p.display_name,
        sub: p.capacities.length > 0 ? p.capacities.join(" · ") : undefined,
      }));
  }
  if (subjectType === "adjacent_party") {
    return parties
      .filter((p) => p.role !== "plaintiff" && p.role !== "defendant")
      .map((p) => ({
        key: p.party_id,
        display_name: p.display_name,
        sub: p.role,
      }));
  }
  if (subjectType === "opposing_counsel") {
    const attorneys = parsedMetadata?.attorneys ?? [];
    // Opposing counsel = those on the plaintiff side (defense-team assumption)
    return attorneys
      .filter((a) => a.side === "plaintiff")
      .map((a, i) => ({
        key: `atty-${i}`,
        display_name: a.name,
        sub: [a.firm, a.role, a.bar_state]
          .filter((x): x is string => !!x)
          .join(" · ") || undefined,
      }));
  }
  if (subjectType === "assigned_judge") {
    const judge = parsedMetadata?.judge;
    if (judge && judge.name) {
      return [
        {
          key: "judge-extracted",
          display_name: judge.name,
          sub: judge.title ?? undefined,
        },
      ];
    }
    return [];
  }
  return [];
}

function DispatchForm({
  parties,
  parsedMetadata,
  onSubmit,
  isPending,
  error,
}: {
  parties: GroupedParty[];
  parsedMetadata: Case["parsed_metadata"] | undefined;
  onSubmit: (input: {
    subjectType: RavenSubjectType;
    subjectName: string;
    subjectContext: string;
  }) => void;
  isPending: boolean;
  error?: string;
}) {
  const lex = useLex();
  const [subjectType, setSubjectType] = useState<RavenSubjectType>(
    "opposing_counsel",
  );
  const [subjectName, setSubjectName] = useState("");
  const [subjectContext, setSubjectContext] = useState("");

  const suggestions = suggestionsFor(subjectType, parties, parsedMetadata);
  const isSelfIdentifying = SELF_IDENTIFYING_TYPES.has(subjectType);
  // Name is required UNLESS the type is self-identifying.
  const nameRequired = !isSelfIdentifying;

  function pickType(next: RavenSubjectType) {
    setSubjectType(next);
    setSubjectName("");
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (nameRequired && !subjectName.trim()) return;
    onSubmit({
      subjectType,
      subjectName: subjectName.trim(),
      subjectContext: subjectContext.trim(),
    });
  }

  const namePlaceholder = isSelfIdentifying
    ? lex.ui.ravenIdentifyHint
    : suggestions.length > 0
      ? "Pick a suggestion above, or type a different name"
      : "Type the subject's name";

  return (
    <form
      onSubmit={handleSubmit}
      className="mb-5 rounded border border-blue-bright/40 bg-iron-2/50 px-4 py-4 backdrop-blur-sm"
    >
      <p className="mb-3 font-display text-base font-light uppercase tracking-[0.14em] text-blue-bright">
        // dispatch
      </p>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <label className="block">
          <span className="font-display text-base font-light uppercase tracking-[0.14em] text-bone-mute">
            type
          </span>
          <select
            value={subjectType}
            onChange={(e) => pickType(e.target.value as RavenSubjectType)}
            disabled={isPending}
            className="mt-1 w-full border border-steel-dark bg-iron/80 px-3 py-2 font-display text-sm text-bone focus:border-blue-bright/60 focus:outline-none"
          >
            {SUBJECT_TYPES.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>

        <label className="block md:col-span-2">
          <span className="font-display text-base font-light uppercase tracking-[0.14em] text-bone-mute">
            name
            {isSelfIdentifying ? (
              <span className="ml-2 text-blue-bright/80">
                // optional — auto-identified
              </span>
            ) : null}
          </span>
          <input
            type="text"
            required={nameRequired}
            value={subjectName}
            onChange={(e) => setSubjectName(e.target.value)}
            disabled={isPending}
            placeholder={namePlaceholder}
            className="mt-1 w-full border border-steel-dark bg-iron/80 px-3 py-2 font-display text-sm text-bone placeholder:text-bone-mute/50 focus:border-blue-bright/60 focus:outline-none"
          />
        </label>
      </div>

      {suggestions.length > 0 ? (
        <div className="mt-3">
          <p className="font-display text-base font-light uppercase tracking-[0.14em] text-bone-mute">
            // suggestions from this case
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {suggestions.map((s) => (
              <button
                key={s.key}
                type="button"
                onClick={() => setSubjectName(s.display_name)}
                disabled={isPending}
                className={`flex flex-col items-start border px-3 py-1.5 text-left font-display text-base font-light uppercase tracking-[0.12em] transition-colors ${
                  subjectName === s.display_name
                    ? "border-blue-bright bg-blue-bright/20 text-bone"
                    : "border-steel-dark bg-iron/60 text-bone-dim hover:border-blue-bright/60 hover:text-bone"
                }`}
              >
                <span>{s.display_name}</span>
                {s.sub ? (
                  <span className="mt-0.5 text-sm tracking-[0.1em] text-bone-mute/70">
                    {s.sub}
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <label className="mt-3 block">
        <span className="font-display text-base font-light uppercase tracking-[0.14em] text-bone-mute">
          context (optional)
        </span>
        <input
          type="text"
          value={subjectContext}
          onChange={(e) => setSubjectContext(e.target.value)}
          disabled={isPending}
          placeholder="Firm name, role, jurisdiction — anything to disambiguate"
          className="mt-1 w-full border border-steel-dark bg-iron/80 px-3 py-2 font-display text-sm text-bone placeholder:text-bone-mute/50 focus:border-blue-bright/60 focus:outline-none"
        />
      </label>

      {error ? (
        <p className="mt-3 font-display text-base font-light uppercase tracking-[0.12em] text-ember-bright">
          // {error}
        </p>
      ) : null}

      <div className="mt-4 flex items-center justify-between gap-3">
        <p className="font-display text-base font-light uppercase tracking-[0.12em] text-bone-mute/70">
          // raven flies. expect 30–90s.
        </p>
        <button
          type="submit"
          disabled={isPending || (nameRequired && !subjectName.trim())}
          className="border border-blue-bright/60 bg-blue-bright/20 px-6 py-2 font-display text-base font-light uppercase tracking-[0.16em] text-bone transition-colors hover:bg-blue-bright/40 disabled:cursor-wait disabled:opacity-50"
        >
          {isPending ? lex.ui.ravenFlying : lex.ui.ravenDispatch}
        </button>
      </div>
    </form>
  );
}

function DossierCard({ dossier }: { dossier: RavenDossier }) {
  const [expanded, setExpanded] = useState(false);
  const subjectLabel =
    SUBJECT_TYPES.find((s) => s.value === dossier.subject_type)?.label ??
    dossier.subject_type;

  return (
    <div className="rounded border border-steel-dark bg-iron-2/40 px-4 py-3 backdrop-blur-sm">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-start justify-between gap-3 text-left"
      >
        <div className="min-w-0 flex-1">
          <p className="font-display text-base font-light uppercase tracking-[0.12em] text-blue-bright">
            {subjectLabel}
          </p>
          <p className="mt-1 font-display text-base font-light text-bone">
            {dossier.subject_name}
          </p>
          {dossier.summary ? (
            <p className="mt-2 font-display text-sm leading-relaxed text-bone-dim">
              {dossier.summary}
            </p>
          ) : null}
        </div>
        <span className="shrink-0 font-display text-base font-light uppercase tracking-[0.12em] text-bone-mute">
          {expanded ? "↑ collapse" : "↓ expand"}
        </span>
      </button>

      {expanded ? (
        <div className="mt-4 space-y-3 border-t border-steel-dark/70 pt-3">
          <DossierBody body={dossier.body} />
          {dossier.citations.length > 0 ? (
            <div>
              <p className="font-display text-base font-light uppercase tracking-[0.12em] text-bone-mute">
                citations // {dossier.citations.length}
              </p>
              <ul className="mt-2 space-y-1">
                {dossier.citations.map((c, i) => (
                  <li key={i} className="font-display text-base text-bone-dim">
                    <a
                      href={c.url}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="text-blue-bright hover:underline"
                    >
                      [{i + 1}] {c.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <p className="font-display text-sm font-light uppercase tracking-[0.12em] text-bone-mute/60">
            dispatched{" "}
            {new Date(dossier.dispatched_at).toLocaleString("en-US", {
              dateStyle: "short",
              timeStyle: "short",
            })}
            {dossier.llm_model ? ` // ${dossier.llm_model}` : ""}
          </p>
        </div>
      ) : null}
    </div>
  );
}

function DossierBody({ body }: { body: Record<string, unknown> }) {
  // Fields that get a generic prose/list render
  const known: Array<{ key: string; label: string }> = [
    { key: "background", label: "background" },
    { key: "approach_analysis", label: "approach analysis" },
    { key: "strategic_signals", label: "strategic signals" },
  ];

  return (
    <div className="space-y-3">
      {/* Case history coverage banner (NEW) */}
      {body.case_history_coverage &&
      typeof body.case_history_coverage === "object" ? (
        <CaseHistoryCoverage
          coverage={body.case_history_coverage as Record<string, unknown>}
        />
      ) : null}

      {/* Recent matters table (NEW — was noted_matters) */}
      {Array.isArray(body.recent_matters) && body.recent_matters.length > 0 ? (
        <section>
          <p className="font-display text-base font-light uppercase tracking-[0.12em] text-bone-mute">
            recent matters // {(body.recent_matters as unknown[]).length}
          </p>
          <div className="mt-2 space-y-2">
            {(body.recent_matters as unknown[]).map((m, i) => (
              <MatterCard key={i} matter={m} />
            ))}
          </div>
        </section>
      ) : null}

      {/* Backwards compat: old dossiers had noted_matters not recent_matters */}
      {!body.recent_matters &&
      Array.isArray(body.noted_matters) &&
      body.noted_matters.length > 0 ? (
        <section>
          <p className="font-display text-base font-light uppercase tracking-[0.12em] text-bone-mute">
            noted matters
          </p>
          <div className="mt-1">{renderField(body.noted_matters)}</div>
        </section>
      ) : null}

      {known.map(({ key, label }) => {
        const value = body[key];
        if (value === undefined || value === null) return null;
        return (
          <section key={key}>
            <p className="font-display text-base font-light uppercase tracking-[0.12em] text-bone-mute">
              {label}
            </p>
            <div className="mt-1">{renderField(value)}</div>
          </section>
        );
      })}

      {/* Open questions get special rendering — they may have answers now. */}
      {Array.isArray(body.open_questions) && body.open_questions.length > 0 ? (
        <section>
          <p className="font-display text-base font-light uppercase tracking-[0.12em] text-bone-mute">
            open questions
          </p>
          <div className="mt-2 space-y-3">
            {(body.open_questions as unknown[]).map((q, i) => (
              <OpenQuestionCard key={i} question={q} />
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}

function CaseHistoryCoverage({
  coverage,
}: {
  coverage: Record<string, unknown>;
}) {
  const found =
    typeof coverage.matters_found === "number" ? coverage.matters_found : 0;
  const target =
    typeof coverage.matters_target === "number"
      ? coverage.matters_target
      : 10;
  const limit =
    typeof coverage.limits_explanation === "string"
      ? coverage.limits_explanation
      : null;
  const isThin = found < target;

  return (
    <div
      className={`rounded border px-3 py-2 ${
        isThin
          ? "border-bone-mute/40 bg-iron-2/40"
          : "border-blue-bright/40 bg-iron-2/60"
      }`}
    >
      <p
        className={`font-display text-base font-light uppercase tracking-[0.12em] ${
          isThin ? "text-bone-mute" : "text-blue-bright"
        }`}
      >
        case history coverage // {found} / {target} matters found
      </p>
      {limit ? (
        <p className="mt-1 font-display text-sm leading-relaxed text-bone-dim">
          {limit}
        </p>
      ) : null}
    </div>
  );
}

function MatterCard({ matter }: { matter: unknown }) {
  if (typeof matter !== "object" || matter === null) return null;
  const m = matter as {
    matter?: string;
    case_number?: string | null;
    year?: string;
    court?: string;
    their_role?: string;
    outcome?: string;
    approach_signals?: string;
    source_urls?: string[];
  };

  const outcomeColor =
    m.outcome?.toLowerCase().includes("won") ||
    m.outcome?.toLowerCase().includes("summary judgment for")
      ? "text-blue-bright"
      : m.outcome?.toLowerCase().includes("lost") ||
          m.outcome?.toLowerCase().includes("summary judgment against")
        ? "text-ember-bright"
        : m.outcome?.toLowerCase().includes("settled")
          ? "text-bone"
          : "text-bone-mute";

  return (
    <div className="rounded border border-steel-dark bg-charcoal/30 px-3 py-2">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-display text-sm font-light text-bone">
            {m.matter ?? "(unnamed matter)"}
          </p>
          <p className="mt-0.5 font-display text-base font-light uppercase tracking-[0.1em] text-bone-mute">
            {[m.year, m.court, m.their_role].filter(Boolean).join(" // ")}
            {m.case_number ? ` // ${m.case_number}` : ""}
          </p>
        </div>
        {m.outcome ? (
          <span
            className={`shrink-0 rounded border border-current/40 px-2 py-0.5 font-display text-sm font-light uppercase tracking-[0.12em] ${outcomeColor}`}
          >
            {m.outcome}
          </span>
        ) : null}
      </div>
      {m.approach_signals ? (
        <p className="mt-2 font-display text-sm leading-relaxed text-bone-dim">
          <span className="font-display text-sm font-light uppercase tracking-[0.14em] text-bone-mute/80">
            approach //{" "}
          </span>
          {m.approach_signals}
        </p>
      ) : null}
      {m.source_urls && m.source_urls.length > 0 ? (
        <ul className="mt-1 space-y-0.5">
          {m.source_urls.map((u, i) => (
            <li key={i} className="truncate font-display text-base">
              <a
                href={u}
                target="_blank"
                rel="noreferrer noopener"
                className="text-blue-bright hover:underline"
              >
                ↗ {u}
              </a>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function OpenQuestionCard({ question }: { question: unknown }) {
  // Backwards-compat: older dossiers stored open_questions as plain strings.
  if (typeof question === "string") {
    return (
      <div className="rounded border border-steel-dark/70 bg-charcoal/30 px-3 py-2">
        <p className="font-display text-sm leading-relaxed text-bone-dim">
          · {question}
        </p>
      </div>
    );
  }

  if (typeof question !== "object" || question === null) return null;
  const q = question as {
    question?: string;
    answer?: string;
    answer_confidence?: string;
    supporting_sources?: string[];
  };

  const confColor =
    q.answer_confidence === "high"
      ? "text-ember-bright"
      : q.answer_confidence === "medium"
        ? "text-blue-bright"
        : q.answer_confidence === "low"
          ? "text-bone-mute"
          : "text-bone-mute/60"; // unanswered

  return (
    <div className="rounded border border-steel-dark bg-charcoal/40 px-3 py-2.5">
      <p className="font-display text-sm leading-snug text-bone">
        Q: {q.question ?? "(missing question)"}
      </p>
      {q.answer ? (
        <p className="mt-2 font-display text-sm leading-relaxed text-bone-dim">
          <span className={`mr-2 font-display text-sm font-light uppercase tracking-[0.14em] ${confColor}`}>
            {q.answer_confidence ?? "answered"} //
          </span>
          {q.answer}
        </p>
      ) : null}
      {q.supporting_sources && q.supporting_sources.length > 0 ? (
        <ul className="mt-2 space-y-0.5">
          {q.supporting_sources.map((url, i) => (
            <li
              key={i}
              className="truncate font-display text-base tracking-tight"
            >
              <a
                href={url}
                target="_blank"
                rel="noreferrer noopener"
                className="text-blue-bright hover:underline"
              >
                ↗ {url}
              </a>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function renderField(value: unknown): React.ReactNode {
  if (typeof value === "string") {
    return <p className="font-display text-sm leading-relaxed text-bone-dim">{value}</p>;
  }
  if (Array.isArray(value)) {
    return (
      <ul className="space-y-1">
        {value.map((item, i) => (
          <li key={i} className="font-display text-sm leading-relaxed text-bone-dim">
            · {typeof item === "string" ? item : <RenderObject obj={item} />}
          </li>
        ))}
      </ul>
    );
  }
  if (value && typeof value === "object") {
    return <RenderObject obj={value as Record<string, unknown>} />;
  }
  return null;
}

function RenderObject({ obj }: { obj: Record<string, unknown> }) {
  const entries = Object.entries(obj);
  return (
    <div className="space-y-1">
      {entries.map(([k, v]) => (
        <div key={k} className="font-display text-sm leading-relaxed text-bone-dim">
          <span className="font-display text-base font-light uppercase tracking-[0.1em] text-bone-mute">
            {k.replace(/_/g, " ")}:
          </span>{" "}
          {typeof v === "string"
            ? v
            : Array.isArray(v)
              ? v.join("; ")
              : JSON.stringify(v)}
        </div>
      ))}
    </div>
  );
}

// ============================================================
// Reusable bits
// ============================================================

function PanelShell({
  label,
  status,
  children,
}: {
  label: string;
  status: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded border border-steel-dark bg-iron/70 backdrop-blur-sm">
      <div className="flex items-center justify-between border-b border-steel-dark/70 px-5 py-3 font-display text-base font-light uppercase tracking-[0.16em]">
        <span className="text-bone-dim">{label}</span>
        <span className="text-bone-mute/70">// {status}</span>
      </div>
      <div className="px-5 py-4">{children}</div>
    </section>
  );
}

function Skeleton({ label }: { label: string }) {
  return (
    <p className="flex items-center gap-3 font-display text-base font-light uppercase tracking-[0.16em] text-bone-mute">
      <span className="inline-block h-1.5 w-1.5 animate-pulse-ember rounded-full bg-ember-bright shadow-ember-glow" />
      <span className="inline-block h-1.5 w-1.5 animate-pulse-ember rounded-full bg-ember-bright shadow-ember-glow [animation-delay:200ms]" />
      <span className="inline-block h-1.5 w-1.5 animate-pulse-ember rounded-full bg-ember-bright shadow-ember-glow [animation-delay:400ms]" />
      {label}
    </p>
  );
}

function ErrorRow({ message }: { message: string }) {
  return (
    <div>
      <p className="font-display text-base font-light uppercase tracking-[0.16em] text-ember-bright">
        // the lines are cut
      </p>
      <p className="mt-1 font-display text-base text-bone-dim">{message}</p>
    </div>
  );
}

function EmptyRow({
  label,
  cta,
  onCta,
}: {
  label: string;
  cta?: string;
  onCta?: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <p className="font-display text-sm text-bone-dim">{label}</p>
      {cta && onCta ? (
        <button
          type="button"
          onClick={onCta}
          className="border border-ember-bright/60 bg-ember/30 px-4 py-1.5 font-display text-base font-light uppercase tracking-[0.14em] text-bone hover:bg-ember/50 hover:shadow-ember-soft"
        >
          {cta}
        </button>
      ) : null}
    </div>
  );
}

export default Raven;
